import React, { useState, useContext, useEffect } from 'react';
import { withTranslation } from "react-i18next";
import { Form, Row, Col, Breadcrumb } from 'react-bootstrap';
import { toast } from "react-toastify";
import { LoaderContext } from '../../../App';
import AICopilotConfigService from '../../../core/service/AICopilotConfigService';
import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal';
import GeneratePromptModal from '../../../components/GeneratePromptModal/GeneratePromptModal';
import './ReportPrompting.scss';

const ReportPrompting = ({ t }) => {
  const { setLoaderDisplay } = useContext(LoaderContext);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('');
  const [selectedFramework, setSelectedFramework] = useState('');
  const [textArea1, setTextArea1] = useState('');
  const [textArea2, setTextArea2] = useState('');
  const [textArea3, setTextArea3] = useState('');
  const [promptsLoaded, setPromptsLoaded] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [categories, setCategories] = useState([]);
  const [topics, setTopics] = useState([]);
  const [filteredTopics, setFilteredTopics] = useState([]);
  const [showNewTopicInput, setShowNewTopicInput] = useState(false);
  const [newTopicName, setNewTopicName] = useState('');

  const frameworks = [
    { id: 'GRI', name: 'GRI' },
    { id: 'GHG', name: 'GHG' },
  ];

  // Fetch categories and topics when framework is selected
  useEffect(() => {
    const fetchCategoriesTopicsByFramework = async () => {
      if (!selectedFramework) {
        // Clear categories and topics when no framework is selected
        setCategories([]);
        setTopics([]);
        setFilteredTopics([]);
        setSelectedCategory('');
        setSelectedTopic('');
        setPromptsLoaded(false);
        return;
      }

      setLoaderDisplay(true);
      try {
        const response = await AICopilotConfigService.getCategoriesTopicsByFramework(selectedFramework);
        console.log('Categories and topics data for framework:', selectedFramework, response);
        
        if (response && response.data && response.data.categories) {
          // Transform the API response structure to match our expected format
          const transformedCategories = response.data.categories.map(cat => ({
            id: cat.category.toLowerCase(),
            name: cat.category
          }));
          
          const transformedTopics = response.data.categories.flatMap(cat => 
            cat.topics.map(topic => ({
              id: topic,
              name: topic,
              categoryId: cat.category.toLowerCase()
            }))
          );
          
          setCategories(transformedCategories);
          setTopics(transformedTopics);
          setFilteredTopics([]); // Initialize filtered topics as empty
          
          // Clear previous selections when framework changes
          setSelectedCategory('');
          setSelectedTopic('');
          setPromptsLoaded(false);
        }
      } catch (error) {
        console.error('Error fetching categories and topics for framework:', error);
        toast.error(t("ReportPrompting.fetchCategoriesError.label"), {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 5000,
        });
      } finally {
        setLoaderDisplay(false);
      }
    };

    fetchCategoriesTopicsByFramework();
  }, [selectedFramework, setLoaderDisplay, t]);

  // Fetch prompts by framework, category and topic when all are selected
  useEffect(() => {
    const fetchPromptsByCategoryTopic = async () => {
      if (selectedFramework && selectedCategory && selectedTopic) {
        setLoaderDisplay(true);
        try {
          const response = await AICopilotConfigService.getPromptsByCategoryTopic(selectedFramework, selectedCategory, selectedTopic);
          console.log('Prompts by framework/category/topic:', response);
          
          if (response && response.data) {
            // Update text areas with the fetched prompts using new field names
            setTextArea1(response.data.functional_prompt || '');
            setTextArea2(response.data.technical_prompt1 || '');
            setTextArea3(response.data.technical_prompt2 || '');
            setPromptsLoaded(true);
          }
        } catch (error) {
          console.error('Error fetching prompts by framework/category/topic:', error);
          toast.error(t("ReportPrompting.fetchError.label"), {
            position: toast.POSITION.TOP_RIGHT,
            autoClose: 5000,
          });
        } finally {
          setLoaderDisplay(false);
        }
      }
    };

    fetchPromptsByCategoryTopic();
  }, [selectedFramework, selectedCategory, selectedTopic, setLoaderDisplay, t]);

  const handleCategoryChange = (e) => {
    const categoryId = e.target.value;
    setSelectedCategory(categoryId);
    
    // Clear selected topic when category changes
    setSelectedTopic('');
    setPromptsLoaded(false);
    setShowNewTopicInput(false);
    setNewTopicName('');
    
    // Filter topics based on selected category
    if (categoryId) {
      // Assuming topics have a categoryId field to link them to categories
      // If the API structure is different, this logic needs to be adjusted
      const filtered = topics.filter(topic => topic.categoryId === categoryId);
      setFilteredTopics(filtered);
    } else {
      setFilteredTopics([]);
    }
  };

  const handleTopicChange = (e) => {
    const value = e.target.value;
    
    if (value === 'new-topic') {
      setShowNewTopicInput(true);
      setSelectedTopic('');
    } else {
      setShowNewTopicInput(false);
      setSelectedTopic(value);
      setNewTopicName('');
    }
  };

  const handleNewTopicInputChange = (e) => {
    setNewTopicName(e.target.value);
  };

  const handleNewTopicSubmit = () => {
    if (!newTopicName.trim()) {
      toast.warning(t("ReportPrompting.validation.enterTopicName.label"), {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 5000,
      });
      return;
    }

    // Convert to uppercase and remove spaces
    const formattedTopicName = newTopicName.trim().toUpperCase().replace(/\s+/g, '');
    
    // Check if topic already exists
    const existingTopic = topics.find(topic => 
      topic.id === formattedTopicName && topic.categoryId === selectedCategory
    );
    
    if (existingTopic) {
      toast.warning(t("ReportPrompting.validation.topicExists.label"), {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 5000,
      });
      return;
    }

    // Create new topic object
    const newTopic = {
      id: formattedTopicName,
      name: formattedTopicName,
      categoryId: selectedCategory
    };

    // Add to topics array
    setTopics(prevTopics => [...prevTopics, newTopic]);
    
    // Update filtered topics
    setFilteredTopics(prevFiltered => [...prevFiltered, newTopic]);
    
    // Set as selected topic
    setSelectedTopic(formattedTopicName);
    setShowNewTopicInput(false);
    setNewTopicName('');
    
    toast.success(t("ReportPrompting.topicCreated.label"), {
      position: toast.POSITION.TOP_RIGHT,
      autoClose: 3000,
    });
  };

  const handleNewTopicCancel = () => {
    setShowNewTopicInput(false);
    setNewTopicName('');
    setSelectedTopic('');
  };

  const handleFrameworkChange = (e) => {
    const newFramework = e.target.value;
    setSelectedFramework(newFramework);
    
    // Clear all form data when framework changes
    setSelectedCategory('');
    setSelectedTopic('');
    setTextArea1('');
    setTextArea2('');
    setTextArea3('');
    setPromptsLoaded(false);
    setFilteredTopics([]);
    setShowNewTopicInput(false);
    setNewTopicName('');
  };

  const handleSave = async () => {
    if (!selectedCategory || !selectedTopic || !selectedFramework) {
      toast.warning(t("ReportPrompting.validation.selectAllFields.label"), {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 5000,
      });
      return;
    }

    setLoaderDisplay(true);

    try {
      const payload = {
        category: selectedCategory,
        topic: selectedTopic,
        framework: [selectedFramework], // Convert to array as per API spec
        functional_prompt: textArea1, // Functional prompt
        technical_prompt1: textArea2, // Technical Prompt 1
        technical_prompt2: textArea3  // Technical Prompt 2
      };

      const response = await AICopilotConfigService.savePrompts(payload);
      
      // If we reach here without an error, the save was successful
      // Check for response.data.success if it exists, otherwise assume success
      if (response && (response.success || response.data?.success || response.status === 200 || response.status === 201)) {
        toast.success(t("ReportPrompting.saveSuccess.label"), {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 5000,
        });
        // Optionally clear form after successful save
        // handleClear();
      } else {
        toast.error(t("ReportPrompting.saveError.label"), {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 5000,
        });
      }
    } catch (err) {
      console.error('Error saving prompts:', err);
      toast.error(t("ReportPrompting.saveError.label"), {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 5000,
      });
    } finally {
      setLoaderDisplay(false);
    }
  };

  const handleClear = () => {
    setSelectedFramework('');
    setSelectedCategory('');
    setSelectedTopic('');
    setTextArea1('');
    setTextArea2('');
    setTextArea3('');
    setPromptsLoaded(false);
    setCategories([]);
    setTopics([]);
    setFilteredTopics([]);
    setShowNewTopicInput(false);
    setNewTopicName('');
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async (confirmed) => {
    if (!confirmed) {
      setShowDeleteModal(false);
      return;
    }

    if (!selectedCategory || !selectedTopic) {
      toast.warning(t("ReportPrompting.validation.selectCategoryTopic.label"), {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 5000,
      });
      setShowDeleteModal(false);
      return;
    }

    setLoaderDisplay(true);
    setShowDeleteModal(false);

    try {
      const response = await AICopilotConfigService.deletePromptByCategoryTopic(selectedFramework, selectedCategory, selectedTopic);
      
      // If we reach here without an error, the delete was successful
      // Check for response.data.success if it exists, otherwise assume success
      if (response && (response.success || response.data?.success || response.status === 200 || response.status === 204)) {
        toast.success(t("ReportPrompting.deleteSuccess.label"), {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 5000,
        });
        
        // Re-fetch categories and topics to refresh the topic list
        try {
          const refreshResponse = await AICopilotConfigService.getCategoriesTopicsByFramework(selectedFramework);
          if (refreshResponse && refreshResponse.data && refreshResponse.data.categories) {
            // Transform the API response structure to match our expected format
            const transformedCategories = refreshResponse.data.categories.map(cat => ({
              id: cat.category.toLowerCase(),
              name: cat.category
            }));
            
            const transformedTopics = refreshResponse.data.categories.flatMap(cat => 
              cat.topics.map(topic => ({
                id: topic,
                name: topic,
                categoryId: cat.category.toLowerCase()
              }))
            );
            
            setCategories(transformedCategories);
            setTopics(transformedTopics);
          }
        } catch (refreshError) {
          console.error('Error refreshing categories and topics:', refreshError);
          // Don't show error toast for refresh failure, just log it
        }
        
        // Clear the form after successful deletion
        handleClear();
      } else {
        toast.error(t("ReportPrompting.deleteError.label"), {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 5000,
        });
      }
    } catch (err) {
      console.error('Error deleting prompt:', err);
      toast.error(t("ReportPrompting.deleteError.label"), {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 5000,
      });
    } finally {
      setLoaderDisplay(false);
    }
  };

  const handleGenerate = () => {
    if (!selectedCategory || !selectedTopic) {
      toast.warning(t("ReportPrompting.validation.selectCategoryTopicForGenerate.label"), {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 5000,
      });
      return;
    }
    setShowGenerateModal(true);
  };

  const handleTransferPrompts = (prompts) => {
    setTextArea1(prompts.functional_prompt || '');
    setTextArea2(prompts.technical_prompt1 || '');
    setTextArea3(prompts.technical_prompt2 || '');
    setPromptsLoaded(true);
    
    // Ensure filtered topics are set for the selected category
    if (selectedCategory) {
      const filtered = topics.filter(topic => topic.categoryId === selectedCategory);
      setFilteredTopics(filtered);
    }
  };

  return (
    <div className="report-prompting-container" data-testid="report-prompting">
      <div className="List_header">
        <Breadcrumb className="h5">
          <Breadcrumb.Item href="#">{t("ReportPrompting.label")}</Breadcrumb.Item>
          <Breadcrumb.Item active>{t("ReportPrompting.title.label")}</Breadcrumb.Item>
        </Breadcrumb>
      </div>

      <div className="dropdowns-section">
        <Row>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>{t("ReportPrompting.framework.label")}</Form.Label>
              <Form.Select 
                value={selectedFramework} 
                onChange={handleFrameworkChange}
                data-testid="framework-select"
              >
                <option value="">{t("ReportPrompting.selectFramework.label")}</option>
                {frameworks.map(framework => (
                  <option key={framework.id} value={framework.id}>
                    {framework.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>{t("ReportPrompting.category.label")}</Form.Label>
              <Form.Select 
                value={selectedCategory} 
                onChange={handleCategoryChange}
                data-testid="category-select"
                disabled={!selectedFramework}
              >
                <option value="">{t("ReportPrompting.selectCategory.label")}</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>{t("ReportPrompting.topic.label")}</Form.Label>
              {!showNewTopicInput ? (
                <Form.Select 
                  value={selectedTopic} 
                  onChange={handleTopicChange}
                  data-testid="topic-select"
                  disabled={!selectedCategory}
                >
                  <option value="">{t("ReportPrompting.selectTopic.label")}</option>
                  {filteredTopics.map(topic => (
                    <option key={topic.id} value={topic.id}>
                      {topic.name}
                    </option>
                  ))}
                  {selectedCategory && (
                    <option value="new-topic" className="text-primary fw-bold">
                      ➕ {t("ReportPrompting.addNewTopic.label")}
                    </option>
                  )}
                </Form.Select>
              ) : (
                <div className="new-topic-input-container">
                  <Form.Control
                    type="text"
                    value={newTopicName}
                    onChange={handleNewTopicInputChange}
                    placeholder={t("ReportPrompting.newTopic.placeholder.label")}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleNewTopicSubmit();
                      }
                    }}
                    data-testid="new-topic-input"
                    autoFocus
                  />
                  <div className="new-topic-buttons mt-2">
                    <button 
                      className="btn btn-sm btn-success me-2"
                      onClick={handleNewTopicSubmit}
                      data-testid="new-topic-submit"
                    >
                      {t("ReportPrompting.newTopic.submit.label")}
                    </button>
                    <button 
                      className="btn btn-sm btn-secondary"
                      onClick={handleNewTopicCancel}
                      data-testid="new-topic-cancel"
                    >
                      {t("ReportPrompting.newTopic.cancel.label")}
                    </button>
                  </div>
                  <small className="text-muted">
                    {t("ReportPrompting.newTopic.help.label")}
                  </small>
                </div>
              )}
            </Form.Group>
          </Col>
        </Row>
      </div>

      <div className="text-areas-section">
        <Row>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>{t("ReportPrompting.textArea1.label")}</Form.Label>
              <Form.Control
                as="textarea"
                rows={8}
                value={textArea1}
                onChange={(e) => setTextArea1(e.target.value)}
                placeholder={t("ReportPrompting.textArea1.placeholder.label")}
                data-testid="text-area-1"
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>{t("ReportPrompting.textArea2.label")}</Form.Label>
              <Form.Control
                as="textarea"
                rows={8}
                value={textArea2}
                onChange={(e) => setTextArea2(e.target.value)}
                placeholder={t("ReportPrompting.textArea2.placeholder.label")}
                data-testid="text-area-2"
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>{t("ReportPrompting.textArea3.label")}</Form.Label>
              <Form.Control
                as="textarea"
                rows={8}
                value={textArea3}
                onChange={(e) => setTextArea3(e.target.value)}
                placeholder={t("ReportPrompting.textArea3.placeholder.label")}
                data-testid="text-area-3"
              />
            </Form.Group>
          </Col>
        </Row>
      </div>

      <div className="action-buttons">
        <div className="generate-button-section">
          <button 
            className="btn btn-primary" 
            onClick={handleGenerate}
            data-testid="generate-button"
          >
            {t("ReportPrompting.generate.label")}
          </button>
        </div>
        <div className="other-buttons-section">
          <button 
            className="btn btn-secondary me-2" 
            onClick={handleClear}
            data-testid="clear-button"
          >
            {t("ReportPrompting.clear.label")}
          </button>
          <button 
            className="btn btn-outline-primary me-2" 
            onClick={handleSave}
            data-testid="save-button"
          >
            {t("ReportPrompting.save.label")}
          </button>
          {promptsLoaded && (
            <button 
              className="btn btn-danger" 
              onClick={handleDelete}
              data-testid="delete-button"
            >
              {t("ReportPrompting.delete.label")}
            </button>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        key={showDeleteModal ? 'show' : 'hide'}
        modalShow={showDeleteModal}
        messageText={t("ReportPrompting.deleteModal.message.label")}
        callbackModal={handleDeleteConfirm}
        title={t("ReportPrompting.deleteModal.title.label")}
        confirmBtn={t("ReportPrompting.deleteModal.confirm.label")}
        CancelBtn={t("ReportPrompting.deleteModal.cancel.label")}
      />

      {/* Generate Prompt Modal */}
      <GeneratePromptModal
        show={showGenerateModal}
        onHide={() => setShowGenerateModal(false)}
        selectedFramework={selectedFramework}
        selectedCategory={selectedCategory}
        selectedTopic={selectedTopic}
        onTransferPrompts={handleTransferPrompts}
      />
    </div>
  );
};

export default withTranslation()(ReportPrompting);
